<template>
  <VSnackbar
    v-model="showNotification"
    :timeout="-1"
    :color="currentNotification?.isFlagged ? 'error' : 'primary'"
    location="top center"
    multi-line
    vertical
    :style="{ maxWidth: '500px' }"
  >
    <div class="d-flex text-white">
      <div v-if="currentNotification?.thumbnailBase64" class="mr-3 flex-shrink-0">
        <VImg
          :src="`data:image/jpeg;base64,${currentNotification.thumbnailBase64}`"
          width="80"
          height="80"
          cover
          class="rounded"
          :class="{ 'border-error': currentNotification.isFlagged }"
          style="border: 2px solid white;"
        >
          <template #placeholder>
            <div class="d-flex align-center justify-center fill-height">
              <VProgressCircular indeterminate color="white" size="24" />
            </div>
          </template>
          <template #error>
            <div class="d-flex align-center justify-center fill-height bg-grey-darken-3 rounded">
              <VIcon color="white">mdi mdi-account</VIcon>
            </div>
          </template>
        </VImg>
      </div>
      <div class="d-flex flex-column flex-grow-1">
        <div class="d-flex align-center mb-2">
          <VIcon class="mr-2" size="large" color="white">
            {{ currentNotification?.isFlagged ? 'mdi mdi-alert-circle' : 'mdi mdi-account-check' }}
          </VIcon>
          <div class="d-flex align-center justify-space-between flex-grow-1">
            <span class="text-h6 font-weight-bold text-white">
              {{ currentNotification?.isFlagged ? 'Flagged Customer Alert!' : 'Customer Arrival' }}
            </span>
            <VChip v-if="notificationQueue.length > 1" color="white" size="x-small" variant="outlined">
              +{{ notificationQueue.length - 1 }} more
            </VChip>
          </div>
        </div>
        <div v-if="currentNotification">
        <div v-if="currentNotification.customer?.name || currentNotification.customer?.firstName" class="mb-1">
          <strong class="text-white">{{ currentNotification.customer.name || currentNotification.customer.firstName }}</strong>
          <VChip v-if="currentNotification.visitCount > 1" class="ml-2" size="x-small" color="white" variant="flat">
            Visit #{{ currentNotification.visitCount }}
          </VChip>
          <VChip v-if="currentNotification.customer?.loyaltyPoints" class="ml-2" size="x-small" color="amber" variant="flat">
            {{ currentNotification.customer.loyaltyPoints }} pts
          </VChip>
        </div>
        <div v-else-if="currentNotification.visitCount > 1" class="mb-1">
          <strong class="text-white">Returning Customer</strong>
          <VChip class="ml-2" size="x-small" color="white" variant="flat">
            Visit #{{ currentNotification.visitCount }}
          </VChip>
        </div>
        <div v-else class="mb-1">
          <strong class="text-white">New Customer</strong>
        </div>

        <div v-if="currentNotification.isFlagged" class="text-body-2 mt-2 pa-2 bg-error-darken-1 rounded">
          <VIcon size="small" class="mr-1" color="white">mdi mdi-alert</VIcon>
          <strong>Reason:</strong> {{ currentNotification.flagReason || 'Bill runner' }}
        </div>

        <div v-if="currentNotification.lastOrder" class="text-body-2 mt-2 text-white" style="opacity: 0.9">
          Last order: #{{ currentNotification.lastOrder.orderId }} - ${{ formatAmount(currentNotification.lastOrder.totalAmount) }}
        </div>
        </div>
      </div>
    </div>

    <template #actions>
      <VBtn
        v-if="currentNotification?.customer?.id"
        color="white"
        variant="text"
        @click="viewCustomer"
      >
        View
      </VBtn>
      <VBtn
        color="white"
        variant="text"
        @click="dismissNotification"
      >
        {{ notificationQueue.length > 1 ? 'Next' : 'Dismiss' }}
      </VBtn>
    </template>
  </VSnackbar>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { usePosEventBus } from '~/composables/usePosEventBus'
import { usePatronect } from '~/composables/usePatronect'

const emit = defineEmits(['view-customer'])

const { onPatronectCustomerArrival, offPatronectCustomerArrival, emitViewCustomer } = usePosEventBus()
const { patronectEnabled, isConfigured, arrivalCooldownMinutes, loadSettings } = usePatronect()

const showNotification = ref(false)
const notificationQueue = ref([])
const processedFaces = new Map()

const currentNotification = computed(() => notificationQueue.value[0] || null)

const formatAmount = (value) => {
  if (value === null || value === undefined) return '0.00'
  return Number(value).toFixed(2)
}

const handleArrival = (payload) => {
  console.log('[PatronectArrival] handleArrival called:', { enabled: patronectEnabled.value, configured: isConfigured.value, payload })

  if (!patronectEnabled.value || !isConfigured.value) {
    console.log('[PatronectArrival] Skipping - not enabled/configured')
    return
  }

  const cooldownMs = Number(arrivalCooldownMinutes.value || 5) * 60 * 1000
  const now = Date.now()
  const lastShown = processedFaces.get(payload.faceUuid)

  if (lastShown && (now - Number(lastShown)) < Number(cooldownMs) && !payload.isFlagged) {
    console.log('[PatronectArrival] Skipping - shown recently:', payload.faceUuid)
    return
  }

  console.log('[PatronectArrival] Showing notification for:', payload.faceUuid)
  if (!payload.isFlagged) {
    processedFaces.set(payload.faceUuid, now)
  }

  notificationQueue.value.push(payload)
  showNotification.value = true

  if (processedFaces.size > 200) {
    const entries = Array.from(processedFaces.entries())
    processedFaces.clear()
    entries.slice(-100).forEach(([k, v]) => processedFaces.set(k, v))
  }
}

const dismissNotification = () => {
  notificationQueue.value.shift()

  if (notificationQueue.value.length === 0) {
    showNotification.value = false
  }
}

const viewCustomer = () => {
  if (currentNotification.value?.customer?.id) {
    emitViewCustomer(currentNotification.value.customer)
  }
  dismissNotification()
}

onMounted(async () => {
  await loadSettings()
  onPatronectCustomerArrival(handleArrival)
})

onUnmounted(() => {
  offPatronectCustomerArrival(handleArrival)
})
</script>
